// PATH: src/components/modules/devtools/utils/releaseSyncApi.js

const DEFAULT_DIRECTION = "TEST_TO_PRODUCTION";

const getErrorMessage = (payload, fallback) => {
  const error = payload && payload.error;
  if (typeof error === "string" && error.trim()) return error;
  if (error && typeof error === "object") {
    const code = error.code ? `${error.code}: ` : "";
    if (error.message) return `${code}${error.message}`;
  }
  return fallback;
};

const fetchJson = async (url, fallbackMessage, options = {}) => {
  const response = await fetch(url, { method: "GET", ...options });
  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    throw new Error(`${fallbackMessage} (invalid JSON response)`);
  }

  if (!response.ok || !payload || payload.success !== true) {
    throw new Error(getErrorMessage(payload, fallbackMessage));
  }
  return payload;
};

const buildPairParams = ({
  sourceProjectId,
  targetProjectId,
  direction = DEFAULT_DIRECTION,
}) => {
  const params = new URLSearchParams();
  params.set("source_project_id", sourceProjectId || "");
  params.set("target_project_id", targetProjectId || "");
  params.set("direction", direction || DEFAULT_DIRECTION);
  return params;
};

export const fetchReleaseSyncProjects = async () => {
  const payload = await fetchJson(
    "/api/dev-ide/projects",
    "Failed to load DEV IDE projects."
  );
  return payload.projects || [];
};

export const fetchReleaseSyncScan = async ({
  sourceProjectId,
  targetProjectId,
  direction = DEFAULT_DIRECTION,
  includeTextDiffs = false,
  recordObservation = false,
  manualComparison = false,
}) => {
  const params = buildPairParams({ sourceProjectId, targetProjectId, direction });
  params.set("include_text_diffs", includeTextDiffs ? "true" : "false");
  params.set("manual_comparison", manualComparison ? "true" : "false");
  return fetchJson(
    `/api/dev-ide/release-sync/scan?${params.toString()}`,
    "Release Sync scan failed.",
    { method: recordObservation ? "POST" : "GET" }
  );
};

export const fetchReleaseSyncHistory = async ({
  sourceProjectId,
  targetProjectId,
  direction = DEFAULT_DIRECTION,
  includeAligned = false,
  manualComparison = false,
}) => {
  const params = buildPairParams({ sourceProjectId, targetProjectId, direction });
  params.set("include_aligned", includeAligned ? "true" : "false");
  params.set("manual_comparison", manualComparison ? "true" : "false");
  return fetchJson(
    `/api/dev-ide/release-sync/historical-reconciliation?${params.toString()}`,
    "Release Sync historical reconciliation failed."
  );
};

export const fetchReleaseSyncFileDetail = async ({
  sourceProjectId,
  targetProjectId,
  direction = DEFAULT_DIRECTION,
  path,
  manualComparison = false,
}) => {
  const scan = await fetchReleaseSyncScan({
    sourceProjectId,
    targetProjectId,
    direction,
    includeTextDiffs: true,
    manualComparison,
  });

  const history = await fetchReleaseSyncHistory({
    sourceProjectId,
    targetProjectId,
    direction,
    includeAligned: true,
    manualComparison,
  });

  const scanAsset = (scan.assets || []).find((item) => item.path === path) || null;
  const historyAsset =
    (history?.assets || []).find((item) => item.path === path) || null;

  return {
    scanAsset,
    historyAsset,
    evidencePolicy: history?.evidence_policy || null,
  };
};

export { DEFAULT_DIRECTION };
